import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';
import { updateLevelProgress, getLevelHighScore } from '@/config/progress';

interface ResultSceneData {
  score: number;
  streak: number;
  decodedMessage: string;
  intendedMessage: string;
  accuracy: number;
  levelIndex?: number;
  difficultyKey?: string;
}

export class ResultScene extends Phaser.Scene {
  private buttonsEnabled: boolean = false;
  private levelIndex: number | null = null;
  private lastDifficultyKey: string | null = null;
  private levelSuccess: boolean = false;
  private newHighScore: boolean = false;
  private unlockedLevel: number | null = null;

  constructor() {
    super({ key: 'ResultScene' });
  }

  init(data: ResultSceneData): void {
    this.buttonsEnabled = false;
    this.data.set('score', data.score || 0);
    this.data.set('streak', data.streak || 0);
    this.data.set('decodedMessage', data.decodedMessage || '');
    this.data.set('intendedMessage', data.intendedMessage || '');
    this.data.set('accuracy', data.accuracy || 0);
    this.levelIndex = data.levelIndex ?? null;
    this.lastDifficultyKey = data.difficultyKey || null;

    // Determine if level was completed successfully (ignore whitespace differences)
    const normalizeMessage = (s: string) => s.replace(/\s+/g, '');
    this.levelSuccess = normalizeMessage(data.decodedMessage) === normalizeMessage(data.intendedMessage);

    // Update progress and check for unlocks
    if (this.levelIndex !== null) {
      const result = updateLevelProgress(this.levelIndex, data.score || 0, this.levelSuccess);
      this.newHighScore = result.newHighScore;
      this.unlockedLevel = result.unlockedLevel;
    } else {
      this.newHighScore = false;
      this.unlockedLevel = null;
    }
  }

  create(): void {
    // Fade in
    this.cameras.main.fadeIn(300, 5, 7, 22);

    // Arcade background
    drawArcadeBackground(this);

    const score = this.data.get('score') as number;
    const streak = this.data.get('streak') as number;
    const decodedMessage = this.data.get('decodedMessage') as string;
    const intendedMessage = this.data.get('intendedMessage') as string;
    const accuracy = this.data.get('accuracy') as number;

    // Title with glow - different message based on success
    const titleText = this.levelSuccess ? 'TRANSMISSION COMPLETE!' : 'TRANSMISSION FAILED';
    const titleColor = this.levelSuccess ? THEME.colors.green : THEME.colors.hit.ok;
    const title = this.add.text(GAME_WIDTH / 2, 70, titleText, {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.titleLarge,
      color: titleColor,
    }).setOrigin(0.5);
    addTextGlow(title, titleColor, 15);

    // Score with high score underneath
    const scoreY = 140;
    this.add.text(GAME_WIDTH / 2, scoreY, `SCORE: ${score.toLocaleString()}`, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.subheading,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // High score display (always show, right below score)
    const highScore = this.levelIndex !== null ? getLevelHighScore(this.levelIndex) : 0;
    this.add.text(GAME_WIDTH / 2, scoreY + 35, `HIGH SCORE: ${highScore > 0 ? highScore.toLocaleString() : '—'}`, {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0.5);

    if (this.newHighScore) {
      const newHighText = this.add.text(GAME_WIDTH / 2, scoreY + 65, 'NEW HIGH SCORE!', {
        fontFamily: THEME.fonts.heading,
        fontSize: THEME.sizes.body,
        color: THEME.colors.yellow,
      }).setOrigin(0.5);
      addTextGlow(newHighText, THEME.colors.yellow, 10);
    }

    // Stats row
    this.add.text(GAME_WIDTH / 2, 250, `STREAK ${streak}    ACCURACY ${accuracy}%`, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0.5);

    // Message comparison
    this.add.text(GAME_WIDTH / 2, 295, 'TARGET MESSAGE:', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, 322, intendedMessage, {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.heading,
      color: THEME.colors.primary,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, 375, 'TRANSMITTED MESSAGE:', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // Highlight differences
    const decodedColor = decodedMessage === intendedMessage ? THEME.colors.primary : THEME.colors.hit.ok;
    this.add.text(GAME_WIDTH / 2, 402, decodedMessage, {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.heading,
      color: decodedColor,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // Level unlock notification
    if (this.unlockedLevel) {
      const unlockText = this.add.text(GAME_WIDTH / 2, 455, 'NEXT LEVEL UNLOCKED!', {
        fontFamily: THEME.fonts.heading,
        fontSize: THEME.sizes.body,
        color: THEME.colors.cyan,
      }).setOrigin(0.5);
      addTextGlow(unlockText, THEME.colors.cyan, 10);
    }

    // Buttons (delayed to prevent accidental clicks)
    this.time.delayedCall(500, () => {
      this.buttonsEnabled = true;

      const leftButtonText = this.levelSuccess ? 'PLAY NEXT' : 'TRY AGAIN';
      const nextLevelIndex = this.levelSuccess && this.levelIndex !== null ? this.levelIndex + 1 : this.levelIndex;

      this.createButton(GAME_WIDTH / 2 - 120, GAME_HEIGHT - 100, leftButtonText, () => {
        if (this.buttonsEnabled) {
          this.transitionTo('LevelSelectScene', nextLevelIndex !== null ? {
            levelIndex: nextLevelIndex,
          } : undefined);
        }
      });

      this.createButton(GAME_WIDTH / 2 + 120, GAME_HEIGHT - 100, 'MAIN MENU', () => {
        if (this.buttonsEnabled) {
          this.transitionTo('MenuScene');
        }
      });
    });

    // Also allow keyboard navigation after delay
    this.time.delayedCall(500, () => {
      this.input.keyboard?.on('keydown-ENTER', () => {
        if (this.buttonsEnabled) {
          this.transitionTo('LevelSelectScene');
        }
      });
      this.input.keyboard?.on('keydown-ESC', () => {
        if (this.buttonsEnabled) {
          this.transitionTo('MenuScene');
        }
      });
    });
  }

  private createButton(x: number, y: number, text: string, onClick: () => void): void {
    const bg = this.add.rectangle(x, y, 200, 56, THEME.colors.primaryHex, 1);
    bg.setStrokeStyle(3, THEME.colors.primaryDarkHex);

    this.add.text(x, y, text, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.textOnPrimary,
    }).setOrigin(0.5);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(THEME.colors.primaryDarkHex));
    bg.on('pointerout', () => bg.setFillStyle(THEME.colors.primaryHex));
    bg.on('pointerdown', onClick);
  }

  private transitionTo(scene: string, data?: object): void {
    this.buttonsEnabled = false;
    this.cameras.main.fadeOut(200, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(scene, data);
    });
  }
}
